import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { AuthenticatedUser, ReportClauseMaintenance, WorkOrder } from '@quorum/models/xs-resource';
import { ReportClauseMaintenanceQueryParameters } from '@quorum/models/xs-query';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { Employee } from '@quorum/models/xs-resource';
import { ApiService } from 'libs/api/src/lib/api.service';
import { SignaturePadComponent } from 'libs/svc-service-lane-ng-ui/src/lib/signature-pad/signature-pad.component';
import { filter, take, tap } from 'rxjs/operators';
import { BodyReportDamageOverlayRef } from '../body-report-damage/body-report-damage-ref';
import { InspectionService } from '../inspection.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BrowserControlCommunicationService } from '@applications/sha-services';
import { MediaStateService } from '@quorum/sha-media';

@Component({
  selector: 'inspection-body-report',
  templateUrl: './body-report.component.html',
  styleUrls: ['./body-report.component.scss'],
})
export class BodyReportComponent implements OnInit, AfterViewInit {
  @ViewChild('sigPad') signaturePad: SignaturePadComponent;
  @Output() onSignatureCompleted = new EventEmitter<any>();
  @Input() printView: boolean;

  signaturePadOptions: { canvasWidth: number; canvasHeight: number; backgroundColor: string } = {
    // passed through to szimek/signature_pad constructor
    canvasWidth: 720,
    canvasHeight: 100,
    backgroundColor: 'rgba(255, 255, 255, 0)',
  };

  signature = '';
  signatureSaved = false;
  bodyReportUnsaved = false;
  agreementClause = '';
  workOrder$: BehaviorSubject<WorkOrder> = new BehaviorSubject(null);

  height: number;
  selectedElement: any = {};
  tools: any[] = [
    {
      id: 0,
      name: 'Scratch',
      class: 'circle-blue',
      color: 'rgba(25,118,210,.5)',
    },
    {
      id: 1,
      name: 'Dent',
      class: 'circle-red',
      color: 'rgba(255,87,34,.5)',
    },
    {
      id: 2,
      name: 'Crack',
      class: 'circle-green',
      color: 'rgba(67,160,71,.5)',
    },
  ];
  currentTool: any = this.tools[0];
  radius: number = 30;
  x: number = 0;
  y: number = 0;
  lastX: number = 0;
  lastY: number = 0;
  sw: number = 5;
  severityOptions: any = {
    value: 1,
    from: 1,
    to: 3,
    step: 1,
    limits: false,
    css: {
      background: {
        'background-color': 'silver',
        height: '1px',
      },
      before: {},
      default: { 'background-color': 'white' },
      after: { 'background-color': 'green' },
      pointer: {
        'background-color': 'rgb(67,160,71)',
        height: '14px',
        width: '14px',
        top: '-35%',
      },
      range: {
        'background-color': 'rgb(67,160,71)',
        height: '2px',
      },
    },
    callback: function (value: any, elt: any) {
      this.selectedElement.severity = value;
    },
  };

  constructor(
    public inspectionService: InspectionService,
    public mediaService: MediaObserver,
    public apiService: ApiService,
    public authenticationStateService: AuthenticationStateService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private browserControlService: BrowserControlCommunicationService,
    private mediaStateService: MediaStateService
  ) {}

  ngAfterViewInit() {
    // this.changes.observe(this.svgImage.nativeElement, {
    //   attributes: true,
    //   childList: true,
    //   characterData: true,
    // });

    this.inspectionService.getBodyReport(this.route.snapshot.queryParams?.workOrderId).then((bodyReport) => {
      const workOrder = bodyReport.workOrder;
      if (!workOrder) {
        return;
      }

      this.workOrder$.next(workOrder);

      // get body report if we are not in XS, this would be handled in inspection component if in XS
      if (!this.browserControlService.embeddedInXsellerator) {
        /* Check if any data returned for Body Report */
        if (JSON.parse(bodyReport.data) || JSON.parse(bodyReport.data).length) {
          /* Check if this data is in new format object (with signature) or not and assign values to bodyReportObject accordingly */
          this.inspectionService.bodyReportObject.bodyReportElements = JSON.parse(bodyReport.data).bodyReport
            ? JSON.parse(bodyReport.data).bodyReport
            : JSON.parse(bodyReport.data);

          this.inspectionService.bodyReportObject.signature = JSON.parse(bodyReport.data).signature
            ? JSON.parse(bodyReport.data).signature
            : null;

          if (JSON.parse(bodyReport.data).vehicleDirty) {
            this.inspectionService.bodyReportObject.vehicleDirty = JSON.parse(bodyReport.data).vehicleDirty;
          }

          this.inspectionService.bodyReportObject.bodyReportElements.forEach(function (
            element: any,
            index: number,
            array: any[]
          ) {
            array[index].f = 0;
          });
        }
      }

      this.authenticationStateService
        .selectAuthenticatedUser()
        .pipe(take(1))
        .subscribe((user: AuthenticatedUser) => {
          this.mediaStateService.setMediaModelKey({
            vehicleId: workOrder.vehicleId,
            appointmentId: workOrder.appointmentId,
            workOrderId: workOrder.id,
            destinationId: user.user.destination,
            customerFacing: false,
            key: 'bodyReport',
          });
        });

      // Size the canvas before drawing — assigning canvas.width clears it, so a
      // resize after drawImageScaled would wipe the restored signature.
      this.resizeSignatureCanvas();

      if (this.inspectionService.bodyReportObject.signature) {
        // Recreate the signature image if Body Report had an earlier saved one
        this.signature = this.inspectionService.bodyReportObject.signature;
        this.signatureSaved = true;
        this.signaturePad?.drawImageScaled(this.signature);
      }
    });

    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((currentUser) => currentUser != null),
        take(1),
        tap((employee: Employee) => {
          let reportClauseMaintenanceQueryParams = new ReportClauseMaintenanceQueryParameters({
            rooftopId: employee.rooftopId.toString(),
            reportClauseTypeId: '12',
          });
          this.apiService
            .get<ReportClauseMaintenance[]>(`v/1/fixed-operations/report-clause-maintenances`, {
              params: reportClauseMaintenanceQueryParams,
            })
            .subscribe((reportClauses: ReportClauseMaintenance[]) => {
              this.agreementClause =
                reportClauses[0] && reportClauses[0].description
                  ? reportClauses[0].description
                  : "I certify that this report is an accurate representation of the vehicle's condition upon delivery.";
            });
        })
      )
      .subscribe();

    this.selectedElement.severity = 0;

    // this.inspectionService.getBodyReport(this.inspectionService.workOrderId).then((data) => {
    //   this.inspectionService.bodyReportElements = JSON.parse(data.data);

    //   this.inspectionService.bodyReportElements.forEach(function (element, index, array) {
    //     array[index].f = 0;
    //   });

    if (this.inspectionService.bodyReportObject.bodyReportElements.length > 0) {
      this.selectedElement = this.inspectionService.bodyReportObject.bodyReportElements[0];
      this.selectedElement.f = 1;
    }
    // });

    this.inspectionService.listenForToasts().subscribe((toastOptions) => {
      this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
    });
  }

  calculateX(x: number, source: string) {
    let ratio;
    if (source === 'small' && this.mediaService.isActive('gt-xs')) {
      ratio = 1.2; // ratio between sizes of body diagram width based on media query
      return x * ratio;
    }
    if (source === 'small' && !this.mediaService.isActive('gt-xs')) {
      return x;
    }
    if (source === 'large' && this.mediaService.isActive('gt-xs')) {
      return x;
    }
    if (source === 'large' && !this.mediaService.isActive('gt-xs')) {
      ratio = 0.8333333; // ratio between sizes of body diagram width based on media query
      return x * ratio;
    }
  }

  calculateY(y: number, source: string) {
    var vm = this;
    var ratio;

    if (source === 'small' && this.mediaService.isActive('gt-xs')) {
      ratio = 1.2; // ratio between sizes of body diagram height based on media query
      return y * ratio;
    }
    if (source === 'small' && !this.mediaService.isActive('gt-xs')) {
      return y;
    }
    if (source === 'large' && this.mediaService.isActive('gt-xs')) {
      return y;
    }
    if (source === 'large' && !this.mediaService.isActive('gt-xs')) {
      ratio = 0.8333333; // ratio between sizes of body diagram height based on media query
      return y * ratio;
    }
  }

  calculateRadius(r: number) {
    var vm = this;

    if (this.mediaService.isActive('gt-xs')) {
      return r * 0.72;
    } else {
      return r * 0.6;
    }
  }

  setTool(index: number) {
    this.currentTool = this.tools[index];
  }

  selectElement(element: any) {
    element.f = 1;
    this.selectedElement.f = 0;
    this.selectedElement = element;
  }

  getDefaultComment(type: any) {
    switch (type) {
      case 'Scratch':
        return 'Scratch damage';
      case 'Dent':
        return 'Dent damage';
      case 'Crack':
        return 'Crack damage';
    }
  }

  removeElement(element: any) {
    var index = this.inspectionService.bodyReportObject.bodyReportElements.indexOf(element);
    this.inspectionService.bodyReportObject.bodyReportElements.splice(index, 1);
  }

  formatSeverity(selectedElement: any) {
    let retVal: string = '';
    switch (selectedElement.severity) {
      case 1:
        retVal = `Superficial ${selectedElement.type}`;
      case 2:
        retVal = `Minor ${selectedElement.type}`;
      case 3:
        retVal = `Major ${selectedElement.type}`;
    }
    return retVal;
  }

  randomString = function randomString(length: number, chars: any) {
    var result = '';
    for (var i = length; i > 0; --i) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  };

  updateElement = function (element: any) {
    this.inspectionService.bodyReportObject.bodyReportElements.forEach(function (ele: any, index: any, array: any) {
      if (ele.id === element.id) {
        array[index] = element;
      }
    });
  };

  drawSignatureStart() {
    this.signatureSaved = false;
    this.bodyReportUnsaved = true;
    // will be notified of szimek/signature_pad's onBegin event
  }

  drawSignatureComplete() {
    //this.signature = this.signaturePad.toDataURL('image/svg+xml');
    // will be notified of szimek/signature_pad's onEnd event
    //image/svg+xml'
    this.signature = this.signaturePad.toDataURL();
  }

  saveSignature() {
    this.signatureSaved = true;
    this.bodyReportUnsaved = false;
    this.onSignatureCompleted.emit(this.signature);
  }

  clearSignature() {
    this.signatureSaved = false;
    this.bodyReportUnsaved = true;
    this.signaturePad.clear();
    this.signature = '';
    this.inspectionService.bodyReportObject.signature = '';
  }

  draw(e: any) {
    var self = this;
    var draw = true;
    this.inspectionService.bodyReportObject.bodyReportElements.forEach(function (element: any) {
      if (
        Math.sqrt(
          (e.offsetX - this.calculateX(element.x, element.source)) *
            (e.offsetX - this.calculateX(element.x, element.source)) +
            (e.offsetY - this.calculateY(element.y, element.source)) *
              (e.offsetY - this.calculateY(element.y, element.source))
        ) < this.calculateRadius(element.r)
      ) {
        draw = false;
        self.selectedElement.f = 0;
        element.f = 1;
        self.selectedElement = element;
        let target = document.getElementById(self.selectedElement.id);
        let dialogRef: BodyReportDamageOverlayRef = self.inspectionService.open({
          selectedElement: self.selectedElement,
          originElement: target,
        });
        dialogRef.afterClosed().subscribe((selectedElement) => {
          self.selectedElement = selectedElement;
        });
        dialogRef.afterDelete().subscribe((selectedElement) => {
          this.bodyReportUnsaved = true;

          if (this.browserControlService.embeddedInXsellerator) {
            this.inspectionService.unsaved = true;
          }

          self.removeElement(selectedElement);
        });
      }
    }, this);

    if (draw) {
      this.lastX = this.x;
      this.lastY = this.y;
      this.x = e.offsetX;
      this.y = e.offsetY;

      var source = '';
      if (this.mediaService.isActive('gt-xs')) {
        source = 'large';
      } else {
        source = 'small';
      }

      this.inspectionService.bodyReportObject.bodyReportElements.push({
        type: this.currentTool.name,
        x: this.x,
        y: this.y,
        lx: this.lastX,
        ly: this.lastY,
        r: this.radius,
        f: 1,
        sw: this.sw,
        color: this.currentTool.color,
        comment: this.getDefaultComment(this.currentTool.name),
        source: source,
        severity: 1,
        id: this.randomString(8, '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'),
      });
      this.selectedElement.f = 0;
      this.selectedElement = this.inspectionService.bodyReportObject.bodyReportElements.slice(-1).pop();

      const target = {
        getBoundingClientRect: (): any => ({
          bottom: e.clientY,
          height: 0,
          left: e.clientX,
          right: e.clientX,
          top: e.clientY,
          width: 0,
        }),
      };

      const element = new ElementRef(target);
      let dialogRef: BodyReportDamageOverlayRef = self.inspectionService.open({
        selectedElement: self.selectedElement,
        originElement: element,
      });
      dialogRef.afterClosed().subscribe((selectedElement) => {
        self.selectedElement = selectedElement;
      });
      dialogRef.afterDelete().subscribe((selectedElement) => {
        this.bodyReportUnsaved = true;

        if (this.browserControlService.embeddedInXsellerator) {
          this.inspectionService.unsaved = true;
        }

        self.removeElement(selectedElement);
      });

      this.bodyReportUnsaved = true;

      if (this.browserControlService.embeddedInXsellerator) {
        this.inspectionService.unsaved = true;
      }
    }
  }

  deleteBodyReport(workOrder: WorkOrder) {
    const dialogData: TwoActionDialogData = {
      actionTwoCaption: 'Yes',
      actionOneCaption: 'No',
      content: 'Would you like to delete this body report?',
      title: 'Delete Body Report',
    };

    const dialogRef = this.dialog.open(TwoActionDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.clearSignature();
        this.bodyReportUnsaved = false;
        this.signatureSaved = false;
        this.inspectionService.deleteBodyReport();
        this.inspectionService.saveBodyReport(workOrder, null, null, null);
      }
    });
  }

  saveBodyReport(workOrder: WorkOrder) {
    this.saveSignature();
    this.bodyReportUnsaved = false;
    this.inspectionService
      .saveBodyReport(
        workOrder,
        this.inspectionService.bodyReportObject.bodyReportElements,
        this.signature,
        this.inspectionService.bodyReportObject.vehicleDirty
      )
      .then(() => {
        const toastOptions = {
          message: `Body report saved`,
          actionText: '',
          options: { duration: 3000 },
        };

        this.inspectionService.addToast(toastOptions);
      });
  }

  printBodyReport(workOrder: WorkOrder) {
    this.snackBar.open('Preparing body report for printing...', null, {
      duration: 5000,
    });
    setTimeout(() => {
      const divContents = document.getElementById('body-report-container').innerHTML;
      const div = document.createElement('div');
      div.className = 'print-body-report';
      div.innerHTML = divContents;
      div.id = 'TempBodyReport';
      document.body.appendChild(div);
      window.onafterprint = function () {
        console.log('Printing completed...');
        document.getElementById('TempBodyReport')?.remove();
      };
      window.print();
    }, 5000);
  }

  onResize(event: Event) {
    this.resizeSignatureCanvas();

    if (this.signature) {
      this.signaturePad?.drawImageScaled(this.signature);
    }
  }

  private resizeSignatureCanvas(): void {
    const signatureContainer = document.getElementById('signature-container');
    if (!this.signaturePad || !signatureContainer) {
      return;
    }
    const canvasWidth = signatureContainer.offsetWidth - 2;
    if (canvasWidth > 0) {
      this.signaturePad.resizeCanvas(canvasWidth);
    }
  }
}
